'use client'

import { FeatureFlag, useFeatureFlag } from '@/config/featureFlags'
import CardSingle from '@/ui/assets/components/cards-single.inline.svg'
import { cn } from '@/ui/utils/style'
import { Check, ChevronLeft, ChevronRight, Link2, Loader2, Pencil, Plus, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_MAX_CARDS_FALLBACK, getHookTypeLabel } from '../constants'
import type { UserCard } from '../hooks/useUserCards'

interface CardCarouselProps {
  cards: UserCard[]
  selectedCardId: string | undefined
  onSelectCard: (cardId: string) => void
  onAddCard: () => void
  onUpdateLabel: (cardId: string, label: string) => Promise<void>
  isCreatingNewCard: boolean
  showLabel?: boolean
  maxCards?: number
  activeCardNeedsSetup?: boolean
}

export function CardCarousel({
  cards,
  selectedCardId,
  onSelectCard,
  onAddCard,
  onUpdateLabel,
  isCreatingNewCard,
  showLabel = true,
  maxCards,
  activeCardNeedsSetup,
}: CardCarouselProps) {
  const swipeStartX = useRef<number | null>(null)
  const activePointerId = useRef<number | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const showHookBadge = useFeatureFlag(FeatureFlag.CARD_HOOK_MANAGEMENT)

  const effectiveMaxCards = maxCards ?? DEFAULT_MAX_CARDS_FALLBACK
  const canAddCard = cards.length < effectiveMaxCards
  const slides: (UserCard | null)[] = canAddCard ? [...cards, null] : [...cards]
  const totalSlides = slides.length

  const activeCard = activeIndex < cards.length ? cards[activeIndex] : null
  const isAddSlideActive = activeIndex >= cards.length

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, totalSlides - 1))
      setActiveIndex(clamped)

      if (clamped < cards.length) {
        const card = cards[clamped]
        if (card) onSelectCard(card.cardID)
      }
    },
    [cards, onSelectCard, totalSlides],
  )

  useEffect(() => {
    if (isAddSlideActive) return
    const idx = cards.findIndex((c) => c.cardID === selectedCardId)
    if (idx >= 0 && idx !== activeIndex) {
      goTo(idx)
    }
  }, [selectedCardId, cards, activeIndex, isAddSlideActive, goTo])

  const handleSwipeStart = (x: number, pointerId?: number) => {
    swipeStartX.current = x
    if (typeof pointerId === 'number') {
      activePointerId.current = pointerId
    }
  }

  const handleSwipeEnd = (x: number) => {
    if (swipeStartX.current == null) return
    const delta = x - swipeStartX.current
    swipeStartX.current = null
    activePointerId.current = null
    if (Math.abs(delta) < 40) return
    if (delta < 0) goTo(activeIndex + 1)
    if (delta > 0) goTo(activeIndex - 1)
  }

  const resetSwipe = () => {
    swipeStartX.current = null
    activePointerId.current = null
  }

  const getSlideClassName = (relativeIndex: number) => {
    const hasThreeOrMoreSlides = totalSlides >= 3

    if (relativeIndex === 0) {
      return '-translate-x-1/2 -translate-y-1/2 scale-100 rotate-0 opacity-100 z-30'
    }

    if (relativeIndex === -1) {
      return hasThreeOrMoreSlides
        ? '-translate-x-[70%] -translate-y-[53%] scale-[0.88] -rotate-7 opacity-50 z-20 md:-translate-x-[78%] md:-translate-y-[54%] md:scale-[0.9] md:-rotate-8 md:opacity-60'
        : '-translate-x-[64%] -translate-y-[52.5%] scale-[0.9] -rotate-6 opacity-55 z-20 md:-translate-x-[70%] md:-translate-y-[53%] md:scale-[0.92] md:-rotate-7 md:opacity-65'
    }

    if (relativeIndex === 1) {
      return hasThreeOrMoreSlides
        ? '-translate-x-[30%] -translate-y-[53%] scale-[0.88] rotate-7 opacity-50 z-20 md:-translate-x-[22%] md:-translate-y-[54%] md:scale-[0.9] md:rotate-8 md:opacity-60'
        : '-translate-x-[36%] -translate-y-[52.5%] scale-[0.9] rotate-6 opacity-55 z-20 md:-translate-x-[30%] md:-translate-y-[53%] md:scale-[0.92] md:rotate-7 md:opacity-65'
    }

    if (relativeIndex === -2 && hasThreeOrMoreSlides) {
      return '-translate-x-[96%] -translate-y-[56%] scale-[0.8] -rotate-12 opacity-30 z-10 pointer-events-none'
    }

    if (relativeIndex === 2 && hasThreeOrMoreSlides) {
      return '-translate-x-[4%] -translate-y-[56%] scale-[0.8] rotate-12 opacity-30 z-10 pointer-events-none'
    }

    return '-translate-x-1/2 -translate-y-1/2 scale-80 opacity-0 pointer-events-none z-0'
  }

  return (
    <div className="relative">
      <div
        className="relative h-[220px] overflow-hidden sm:h-[250px]"
        style={{ touchAction: 'pan-y' }}
        onPointerDown={(e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return
          handleSwipeStart(e.clientX, e.pointerId)
        }}
        onPointerUp={(e) => {
          if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return
          handleSwipeEnd(e.clientX)
        }}
        onPointerLeave={resetSwipe}
        onPointerCancel={resetSwipe}
        onTouchStart={(e) => handleSwipeStart(e.changedTouches[0]?.clientX ?? 0)}
        onTouchEnd={(e) => handleSwipeEnd(e.changedTouches[0]?.clientX ?? 0)}
      >
        {slides.map((slide, index) => {
          const relativeIndex = index - activeIndex

          if (slide) {
            return (
              <button
                key={slide.cardID}
                type="button"
                onClick={() => goTo(index)}
                className={cn(
                  'absolute top-1/2 left-1/2 w-full max-w-[300px] transform transition-all duration-500 ease-out sm:max-w-[360px]',
                  totalSlides <= 1 && 'cursor-default',
                  getSlideClassName(relativeIndex),
                )}
              >
                <div className="relative overflow-hidden rounded-2xl">
                  <CardSingle
                    className={cn(
                      'h-auto w-full transition-all duration-500',
                      relativeIndex === 0
                        ? 'drop-shadow-[0_8px_24px_rgba(0,0,0,0.25)]'
                        : 'drop-shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
                      relativeIndex === 0 && activeCardNeedsSetup && 'opacity-80 grayscale',
                    )}
                  />
                  {relativeIndex === 0 && (
                    <div className="pointer-events-none absolute bottom-[6%] left-[19.5%] flex items-baseline gap-3 font-medium text-black text-xs">
                      <span style={{ letterSpacing: '0.12em' }}>∙∙∙∙</span>
                      <span>{slide.last4}</span>
                    </div>
                  )}
                </div>
              </button>
            )
          }

          return (
            <button
              key="add-card"
              type="button"
              onClick={() => (index === activeIndex ? onAddCard() : goTo(index))}
              aria-label="Add new card"
              className={cn(
                'absolute top-1/2 left-1/2',
                'aspect-[1.586/1] w-full',
                'max-w-[300px] sm:max-w-[360px]',
                'rounded-2xl',
                'border-2',
                'border-dashed',
                'border-border/60',
                'bg-muted/20',
                'transition-all',
                'duration-500',
                'ease-out',
                'hover:border-primary/50',
                'hover:bg-muted/40',
                getSlideClassName(relativeIndex),
              )}
            >
              <div className="flex h-full w-full flex-col items-center justify-center gap-3">
                {isCreatingNewCard ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">Creating card...</span>
                  </>
                ) : (
                  <>
                    <Plus className="h-8 w-8 text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">Add New Card</span>
                  </>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {showHookBadge && !!activeCard?.hookType && (
        <div className="mt-1 flex justify-center">
          <span
            className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
            title={`Hook: ${getHookTypeLabel(activeCard.hookType)}`}
          >
            <Link2 className="h-3 w-3" />
            {getHookTypeLabel(activeCard.hookType)}
          </span>
        </div>
      )}
      {activeCard && showLabel && <CardLabel card={activeCard} onUpdateLabel={onUpdateLabel} />}

      {isAddSlideActive && !activeCard && (
        <div className="mt-1 flex min-h-[36px] items-center justify-center">
          <button
            type="button"
            onClick={onAddCard}
            className="rounded-md border border-border px-3 py-1.5 text-muted-foreground text-sm transition-colors hover:border-primary/50 hover:text-foreground"
          >
            Add New Card
          </button>
        </div>
      )}

      {totalSlides > 1 && (
        <>
          <button
            onClick={() => goTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            className="-translate-y-1/2 absolute top-[110px] left-0 rounded-full bg-background/80 p-1.5 shadow-md backdrop-blur-sm transition-opacity hover:bg-background disabled:opacity-0"
            aria-label="Previous card"
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>
          <button
            onClick={() => goTo(activeIndex + 1)}
            disabled={activeIndex === totalSlides - 1}
            className="-translate-y-1/2 absolute top-[110px] right-0 rounded-full bg-background/80 p-1.5 shadow-md backdrop-blur-sm transition-opacity hover:bg-background disabled:opacity-0"
            aria-label="Next card"
          >
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>
        </>
      )}

      {totalSlides > 1 && (
        <div className="mt-1.5 flex items-center justify-center gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={cn(
                'h-2 rounded-full transition-all',
                i === activeIndex ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50',
              )}
              aria-label={i < cards.length ? `Card ${i + 1}` : 'Add new card'}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CardLabel({
  card,
  onUpdateLabel,
}: {
  card: UserCard
  onUpdateLabel: (cardId: string, label: string) => Promise<void>
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(card.label ?? '')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    setDraft(card.label ?? '')
  }, [card.label])

  const save = async () => {
    const trimmed = draft.trim()
    if (trimmed === (card.label ?? '')) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onUpdateLabel(card.cardID, trimmed)
      setIsEditing(false)
    } catch {
      setDraft(card.label ?? '')
    } finally {
      setIsSaving(false)
    }
  }

  const cancel = () => {
    setDraft(card.label ?? '')
    setIsEditing(false)
  }

  const displayLabel = card.label || 'Card'

  if (isEditing) {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 50))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') cancel()
          }}
          disabled={isSaving}
          placeholder={`Card ••${card.last4}`}
          className="w-40 rounded border border-primary/50 bg-transparent px-2 py-0.5 text-center text-foreground text-sm focus:outline-none"
          maxLength={50}
        />
        <button
          onClick={save}
          disabled={isSaving}
          className="rounded p-0.5 text-green-400 transition-colors hover:bg-green-400/10"
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button onClick={cancel} className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div className="group flex items-center justify-center gap-1.5">
      <span className="text-muted-foreground text-sm">{displayLabel}</span>
      <button
        onClick={() => setIsEditing(true)}
        className="rounded p-0.5 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  )
}
